import { SettlementStage } from "../types/card-movements.types";

/**
 * Drops the settlement echo of a purchase the issuer reports in two phases: it arrives as a second movement
 * with its own id (authorization first, then settlement at the final FX rate), so the same purchase would
 * render twice. Only the authorization is shown.
 *
 * Filtering here is safe on two counts: the card balance is read from the provider, not derived from these
 * rows, and the backend only stamps `SETTLEMENT` once it has found the authorization the row settles — an
 * orphan settlement arrives unstaged and therefore stays visible.
 *
 * Pure and order-preserving so callers can derive it at render time and keep paginating over the unfiltered
 * list.
 */
export function visibleCardMovements<T extends { settlement_stage: SettlementStage | null }>(
  movements: T[],
): T[] {
  return movements.filter((movement) => movement.settlement_stage !== SettlementStage.SETTLEMENT);
}

/**
 * Formats movement date and time in a friendly Spanish format. Example: "19 de enero de 2025, 05:11"
 *
 * @param dateInput - ISO string or Date (e.g. tx.created_at)
 * @returns Complete date: day, month name, year and time (HH:mm)
 */
export function formatMovementDateTime(dateInput: string | Date): string {
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  const datePart = date.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const timePart = date.toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${datePart}, ${timePart}`;
}
